import Favico from 'favicon-notification';
import { config } from 'lib-shared-backend-config';
import { NotificationT } from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import pick from 'lodash/pick';
import moment from 'moment-timezone';
import Notify from 'notifyjs';
import { delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { history } from 'bootstrap.store';
import { sanitizeHtml } from 'utils/sanitizeHtml';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { selectIsPrimaryConnection } from '../../../app/middleware/websocket/selectors/websocket';
import AudioManager from '../../../app/utils/audioManager';
import FavicoAway from '../../../images/favicon-away.png';
import FavicoOnlineImage from '../../../images/favicon-online.png';
import FavicoImage from '../../../images/favicon.png';
import * as personalActions from '../../settings/actions/personal';
import { getRequestID } from '../../shared/util/request';
import { notificationActions } from '../actions/notification';

import { NOTIFICATION_DEFAULT_FILE } from './audio';

const playSoundToPersonalSettingsMap = {
  'offer-ringing-phone': 'pstnphone',
  'offer-ringing-chat': 'widgetchat',
  'offer-ringing-email': 'email',
  'offer-ringing-messaging': 'messaging',
  notification_sound: 'notification',
};

export type NotificationPlaySoundType =
  keyof typeof playSoundToPersonalSettingsMap;

const { personalSoundSucceeded } = personalActions.actions;
const {
  notificationAdd,
  notificationDesktopAdd,
  notificationRatingAdded,
  notificationAdded,
  notificationTabBadgeShow,
  notificationTabBadgeReset,
  notificationFaviconChange,
  notificationSoundPlay,
  notificationRemoveById,
  notificationRemoveInStateById,
  notificationRemoveByType,
  notificationRemoveInStateByType,
  notificationSoundsInit,
} = notificationActions;

const { twilioEstablishedCall } = twilioActions;

// Init favicon.
Favico.init({
  url: FavicoImage,
});

if (window.Notification && window.Notification.permission !== 'granted') {
  Notify.requestPermission(_premissionGranted, _permissionDenied);
}

function _premissionGranted(permission: string) {
  if (permission !== 'granted') {
    console.warn('Desktop notification denied');
  } else {
    console.info('Desktop notification granted');
  }
}

function _permissionDenied(permission: string) {
  if (permission !== 'granted') {
    console.warn('Desktop notification denied');
  } else {
    console.info('Desktop notification granted');
  }
}

// Load save sound files.
function* personalSoundSucceededGen() {
  AudioManager.stop();
  yield notificationResumeOfferSoundPlay();
}

function* notificationResumeOfferSoundPlay() {
  const getNotifications = (state: any) => state.notifications;
  const notificationsState = yield select(getNotifications);
  for (const notification of notificationsState.currentNotifications) {
    if (notification) {
      if (
        typeof notification.playSound === 'string' &&
        notification.playSound.indexOf('offer-ringing') > -1
      ) {
        yield notificationPlaySound(notification);
      }
    }
  }
}

// Saga
export const notifications = [
  takeEvery(notificationAdd.toString(), notificationsAddedGen),
  takeLatest(notificationTabBadgeShow.toString(), notificationTabBadgeShowGen),
  takeLatest(
    notificationTabBadgeReset.toString(),
    notificationTabBadgeResetGen,
  ),
  takeLatest(
    notificationFaviconChange.toString(),
    notificationFaviconChangeGen,
  ),
  takeLatest(notificationSoundPlay.toString(), notificationSoundPlayGen),
  takeEvery(notificationRemoveById.toString(), notificationBeforeRemoveByIdGen),
  takeEvery(
    notificationRemoveByType.toString(),
    notificationBeforeRemoveByTypeGen,
  ),
  takeEvery(twilioEstablishedCall.toString(), notificationOfferSoundPauseGen),
  takeEvery(notificationDesktopAdd.toString(), notificationDesktopAddGen),
  takeLatest(
    notificationSoundsInit.toString(),
    notificationResumeOfferSoundPlay,
  ),
  takeEvery(personalSoundSucceeded.toString(), personalSoundSucceededGen),
];

const getStatusFromClassName = (className: string) => {
  if (className) {
    if (className.includes('success')) {
      return 'success';
    }
    if (className.includes('warning')) {
      return 'warning';
    }
    if (className.includes('danger')) {
      return 'danger';
    }
  }

  return 'default';
};

export function newNotification(payload: any): NotificationT | null {
  if (!payload) {
    console.error(
      'newNotification payload or payload.options params not given.',
    );
    return null;
  }

  const unixtime = new Date().getTime();

  const className =
    typeof payload.className === 'string' ? payload.className : '';
  const status = payload.status
    ? payload.status
    : getStatusFromClassName(className);

  return {
    ...pick(payload, [
      'text',
      'leftAction',
      'rightAction',
      'banner',
      'hideCloseAction',
      'skipNotificationSound',
    ]),
    id: payload.id !== undefined ? payload.id : getRequestID(),
    title: payload.title !== undefined ? payload.title : '',
    subText: payload.subText !== undefined ? payload.subText : '',
    type: payload.type !== undefined ? payload.type : null,
    conversation:
      payload.conversation !== undefined ? payload.conversation : null,

    icon: payload.icon !== undefined ? payload.icon : 'i-info',
    img: payload.img !== undefined ? payload.img : null,
    className,
    status,
    timestamp:
      payload.timestamp !== undefined ? payload.timestamp : moment().format(),
    showDesktop: payload.showDesktop !== undefined ? payload.showDesktop : true,
    showInpage: payload.showInpage !== undefined ? payload.showInpage : true,

    startTime:
      payload.time && isNaN(payload.time) === false ? payload.time : null,
    actionOnTimeout:
      payload.actionOnTimeout !== undefined ? payload.actionOnTimeout : null,

    date: moment().format(),

    shouldBeRemovedAt:
      payload.time && isNaN(payload.time) === false
        ? unixtime + payload.time
        : null,
    waitingTime: payload.waitingTime !== undefined ? payload.waitingTime : null,
    reason: payload.reason ? payload.reason : null,

    mainAction: payload.mainAction !== undefined ? payload.mainAction : null,
    closeAction:
      payload.closeAction !== undefined &&
      typeof payload.closeAction === 'function'
        ? payload.closeAction
        : null,
    playSound: payload.playSound !== undefined ? payload.playSound : null,
  };
}

function* notificationOfferSoundPauseGen() {
  const getNotifications = (state: any) => state.notifications;
  const notificationsState = yield select(getNotifications);
  for (const notification of notificationsState.currentNotifications) {
    if (notification) {
      if (
        typeof notification.playSound === 'string' &&
        notification.playSound.indexOf('offer-ringing') > -1
      ) {
        AudioManager.stop();
      }
    }
  }
}

function* notificationBeforeRemoveByIdGen(action: any) {
  const { id } = action.payload;
  const getNotifications = (state: any) => state.notifications;
  const notificationsState = yield select(getNotifications);
  const notification = notificationsState.currentNotifications.find(
    (n: any) => n.id === id,
  );

  if (notification) {
    if (
      typeof notification.playSound === 'string' &&
      notification.playSound.indexOf('offer-ringing') > -1
    ) {
      AudioManager.stop();
    }
  }
  yield put(notificationRemoveInStateById(action.payload));
}

function* notificationBeforeRemoveByTypeGen(action: any) {
  const { type } = action.payload;
  const getNotifications = (state: any) => state.notifications;
  const notificationsState = yield select(getNotifications);
  for (const notification of notificationsState.currentNotifications) {
    if (notification) {
      if (
        notification.type === type &&
        typeof notification.playSound === 'string' &&
        notification.playSound.indexOf('offer-ringing') > -1
      ) {
        AudioManager.stop();
      }
    }
  }
  yield put(notificationRemoveInStateByType(action.payload));
}

function* notificationSoundPlayGen(action: any) {
  const override =
    action &&
    action.payload &&
    action.payload.force !== undefined &&
    action.payload.force === true
      ? action.payload.force
      : false;

  const getTelephony = (state) => state.telephony;
  const telephonyState = yield select(getTelephony);
  // Check that we do not have an active phone call.

  if (telephonyState.currentCall !== null) {
    return;
  }

  // Check visibility state with select.
  const getVisibility = (state) => state.visibility;
  const visibilityState = yield select(getVisibility);

  if (override === true || visibilityState.isHidden === true) {
    const playSound = yield select(
      (state) => state.settings.personal.sounds.notification['tone'],
    );

    AudioManager.play(playSound, 'notification_sound', false);
  }
}

function* notificationTabBadgeShowGen(action: any) {
  try {
    const force =
      action.payload && action.payload.force ? action.payload.force : false;

    // Check visibility state with select.
    const getVisibility = (state) => state.visibility;
    const visibilityState = yield select(getVisibility);

    if (force === true || visibilityState.isHidden === true) {
      Favico.add();
    }
  } catch (e) {
    console.error('notificationTabBadgeShowGen', e);
  }
}

function* notificationFaviconChangeGen(action: any) {
  const { type } = action.payload;

  let icon = FavicoImage;
  const titleArr = document.title.split('|');
  let title = '';

  if (type === 'working') {
    icon = FavicoOnlineImage;
    title = 'Working | ';
  } else if (type === 'away') {
    icon = FavicoAway;
    title = 'Away | ';
  } else {
    icon = FavicoImage;
    title = 'Offline | ';
  }
  if (titleArr.length > 1) {
    document.title = title + titleArr[titleArr.length - 1];
  } else {
    document.title = title + document.title;
  }

  Favico.init({
    url: icon,
  });

  const getNotifications = (state) => state.notifications;
  const notificationsState = yield select(getNotifications);
  if (notificationsState.ui.showBadge === true) {
    yield delay(1000); // we delay the change because not all browsers can keep.
    Favico.add();
  }
}

function notificationTabBadgeResetGen() {
  try {
    Favico.remove();
  } catch (e) {
    console.error('notificationTabBadgeResetGen', e);
  }
}

function* notificationPlaySound(notification: any) {
  const isPrimaryConnectionState = yield select(selectIsPrimaryConnection);

  if (notification.playSound && isPrimaryConnectionState) {
    const playSound = yield select((state: any) => {
      const {
        settings: {
          personal: { sounds: personalSounds },
        },
      } = state;
      if (
        notification.playSound &&
        personalSounds &&
        personalSounds[playSoundToPersonalSettingsMap[notification.playSound]]
      ) {
        return (
          personalSounds[playSoundToPersonalSettingsMap[notification.playSound]]
            .tone || NOTIFICATION_DEFAULT_FILE
        );
      }
      return NOTIFICATION_DEFAULT_FILE;
    });

    const channel = notification.conversation
      ? notification.conversation.channel
      : 'pstnphone';

    AudioManager.play(playSound, channel, true);
  }
}

export function* notificationsAddedGen(action: any) {
  try {
    const notification = newNotification(action.payload);
    if (notification === null) return;
    // Push the new notification to the reducer.
    if (
      notification.type &&
      (notification.type === 'rating-up' || notification.type === 'rating-down')
    ) {
      yield put(notificationRatingAdded({ notification: notification }));
    } else {
      yield put(notificationAdded({ notification: notification }));
    }

    // Play custom sound.
    if (
      typeof notification.playSound === 'string' &&
      notification.playSound.indexOf('offer-ringing') > -1
    ) {
      // Check that we do not have an active phone call.
      const getTelephony = (state: any) => state.telephony;
      const telephonyState = yield select(getTelephony);

      // We have an active call. So we ignore.
      if (telephonyState && !telephonyState.currentCall) {
        yield notificationPlaySound(notification);
      }
    }

    if (!notification.skipNotificationSound) {
      yield put(notificationSoundPlay()); // Play new notification sound.
    }

    yield put(notificationTabBadgeShow()); // Show badge - only if tab not is visible.

    // Add notification to notification history.
    const getVisibility = (state: any) => state.visibility;
    const visibilityState = yield select(getVisibility);
    const unseen =
      notification.showDesktop === true && visibilityState.isHidden === true;
    const isPrimaryConnectionState = yield select(selectIsPrimaryConnection);

    if (unseen && isPrimaryConnectionState) {
      // Show desktop notification.
      showDesktopNotification(notification);
    }
  } catch (exception) {
    Sentry.captureException(new Error('notificationsAdded saga exception'), {
      extra: { exception },
    });
  }
}

function* notificationDesktopAddGen(action: any) {
  const notification = newNotification(action.payload);
  if (notification === null) return;
  const getVisibility = (state: any) => state.visibility;
  const visibilityState = yield select(getVisibility);

  if (notification.showDesktop === true && visibilityState.isHidden === true) {
    // Show desktop notification.
    showDesktopNotification(notification);
  }
}

function showDesktopNotification(notification: any) {
  // show desktop notification.
  let image = `${config.url.assets}/dist/icons/png/dixa.png`;

  const type = notification.type ? notification.type : null;
  const time = notification.startTime ? notification.startTime / 1000 : 30;
  const mainAction =
    notification.mainAction && typeof notification.mainAction === 'function'
      ? notification.mainAction
      : window.focus();
  const icon =
    notification.icon && typeof notification.icon === 'string'
      ? `${notification.icon.replace('i-', '')}`
      : null;
  image =
    icon && typeof icon === 'string'
      ? `${config.url.assets}/dist/icons/png/${icon}.png`
      : image;
  const onNotifyClick =
    notification.mainAction &&
    typeof notification.mainAction === 'object' &&
    notification.mainAction.navigate &&
    typeof notification.mainAction.navigate === 'string'
      ? () => {
          history.push(notification.mainAction.navigate);
          window.focus();
        }
      : mainAction;

  const desktopNotificationOptions = {
    body: sanitizeHtml(notification.text, 'plainText'),
    icon: image,
    tag: type,
    closeOnClick: true,
    notifyClick: onNotifyClick,
    requireInteraction: false,
    timeout: time,
  };

  try {
    const desktopNotification = new Notify(
      notification.title,
      desktopNotificationOptions,
    );
    if (Notify.needsPermission) {
      Notify.requestPermission(_premissionGranted, _permissionDenied);
    } else {
      desktopNotification.show();
    }
  } catch (exception) {
    console.error('Desktop exception ', exception);
  }
}

export default notifications;
